import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Button from '../../BaseComponents/Button';
import Select from '../../BaseComponents/Select';
import './BiomarkersTab.scss';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import BiomarkersTable from './BiomarkersTable';
import { changeUpdateBiomarkerModalVisibility } from '../../../store/appSlice';
import { UpdateBiomarkerModal } from './UpdateBiomarkerModal';
import { getBiomarkersList } from '../../../api/biomarkers';
import { setBiomarkersList, setBiomarkersCount } from '../../../store/appSlice';

const BiomarkersTab = () => {
  const dispatch = useDispatch();
  const [summaryFilter, setSummaryFilter] = useState<any>({ key: 'all', value: 'All Summaries' });
  const { biomarkersList, isUpdateBiomarkerModalOpened } = useSelector(
    (state: RootState) => state.app
  );

  const summaryFilterOptions = [
    { key: 'all', value: 'All Summaries' },
    { key: 'age_dependent', value: 'Age Dependent' },
    { key: 'lifestyle_changes', value: 'Lifestyle Changes' },
    { key: 'follow_up', value: 'Follow Up' },
    { key: 'contraindications', value: 'Contraindications' },
    { key: 'measurement_unit', value: 'Measurement Unit' },
  ];

  // Calculate filtered count
  const filteredCount = useMemo(() => {
    if (!summaryFilter || summaryFilter.key === 'all') {
      return biomarkersList.length;
    }

    return biomarkersList.filter((biomarker) => {
      switch (summaryFilter.key) {
        case 'age_dependent':
          return biomarker.age_dependent === true;
        case 'lifestyle_changes':
          return biomarker.lifestyle_changes && biomarker.lifestyle_changes.trim() !== '';
        case 'follow_up':
          return biomarker.follow_up && biomarker.follow_up.trim() !== '';
        case 'contraindications':
          return biomarker.contraindications && biomarker.contraindications.trim() !== '';
        case 'measurement_unit':
          return biomarker.measurement_unit && biomarker.measurement_unit.trim() !== '';
        default:
          return true;
      }
    }).length;
  }, [biomarkersList, summaryFilter]);

  const onAddBiomarkerBtnClick = () => {
    dispatch(changeUpdateBiomarkerModalVisibility(true));
  };

  const onSummaryFilterChange = (option: any, name: string) => {
    setSummaryFilter(option);
  };

  const fetchBiomarkersList = useCallback(async (search?: string) => {
    const updatedBiomarkersList = await getBiomarkersList(search);
    dispatch(setBiomarkersList(updatedBiomarkersList.biomarkers));
    dispatch(setBiomarkersCount(updatedBiomarkersList.biomarkers.length));
  }, [dispatch]);

  useEffect(() => {
    fetchBiomarkersList();
  }, [fetchBiomarkersList]);

  // Handle search with filter
  const handleSearch = (searchTerm: string) => {
    fetchBiomarkersList(searchTerm);
  };

  return (
    <div>
      <div className="search-row-container">
        <div className="search-and-filter-container d-flex align-items-center gap-20">
          <div className="summary-filter-container">
            <Select
              title=""
              options={summaryFilterOptions}
              selectedOption={summaryFilter}
              onChange={onSummaryFilterChange}
              placeholder="Filter by Summary"
              name="summaryFilter"
              isError={false}
            />
          </div>
          <div className="search-input-container" style={{
            flexGrow: "initial"
          }}>
            <input
              type="text" 
              className="search-input"
              placeholder="Search biomarkers..."
              onChange={(e) => {
                handleSearch(e.target.value);
              }}
            />
          </div>
          <div className="count-container">
            {filteredCount} {filteredCount === 1 ? 'Biomarker' : 'Biomarkers'}
            {summaryFilter.key !== 'all' && ` (filtered from ${biomarkersList.length})`}
          </div>
        </div>
        <Button
          isSecondary={true}
          text="Add biomarker"
          onClick={onAddBiomarkerBtnClick}
        />
      </div>
      <BiomarkersTable summaryFilter={summaryFilter} />
      {isUpdateBiomarkerModalOpened && <UpdateBiomarkerModal />}
    </div>
  );
};

export default BiomarkersTab;
